import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Clock, Layers, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { Reveal, SectionHead } from "../components/Reveal";
import Seo from "../components/Seo";
import { GALLERY_FILTERS, API } from "../data";

export default function Galleria() {
  const [filter, setFilter] = useState("Tutti");
  const [active, setActive] = useState(null);
  const [uploads, setUploads] = useState(null);

  useEffect(() => {
    axios.get(`${API}/foto`).then((r) => setUploads(r.data)).catch(() => setUploads([]));
  }, []);

  const all = (uploads || []).map((u) => ({
    key: u.id,
    img: `${API}/foto/file/${u.storage_path}`,
    title: u.titolo,
    cat: u.categoria,
    loc: "Cantiere Edilkrea",
    durata: "Realizzato da Edilkrea",
    materiali: "Foto reale del cantiere",
    desc: "Lavoro realizzato da Edilkrea Technoimpianti.",
  }));

  const visible = filter === "Tutti" ? all : all.filter((p) => p.cat === filter);

  return (
    <main data-testid="galleria-page" className="pt-20">
      <Seo
        title="Galleria Lavori e Cantieri | Edilkrea Technoimpianti"
        description="Le foto reali dei nostri cantieri: ristrutturazioni, interni di design, impiantistica e nuove costruzioni realizzate da Edilkrea Technoimpianti."
      />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-24 pb-10">
        <SectionHead level="h1" overline="Portfolio" title="Vetrina Lavori" />
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
            I nostri cantieri fotografati dal vivo: ristrutturazioni, interni di pregio,
            impiantistica e nuove costruzioni. Tocca un progetto per scoprire i dettagli.
          </p>
        </Reveal>

        <Reveal delay={0.25} className="mt-10 flex flex-wrap gap-2" y={16}>
          {GALLERY_FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`gallery-filter-${f === "Tutti" ? "all" : f.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all ${
                filter === f
                  ? "bg-gold text-ink"
                  : "border border-gold/20 text-cream/70 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
        <motion.div layout className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.button
                layout
                key={p.key}
                data-testid={`gallery-item-${p.title.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setActive(p)}
                className="group relative h-72 sm:h-80 overflow-hidden rounded-2xl border border-gold/15 text-left"
              >
                <img src={p.img} alt={p.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                <span className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-ink/60 backdrop-blur border border-gold/20 text-gold opacity-0 transition-opacity group-hover:opacity-100">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
                <div className="absolute bottom-0 p-5">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-copper">{p.cat}</p>
                  <h3 className="mt-1 font-display text-lg font-bold uppercase text-cream">{p.title}</h3>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        {uploads !== null && visible.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gold/20 p-16 text-center" data-testid="gallery-empty">
            <p className="font-display text-xl font-bold uppercase text-cream">Nessun lavoro in questa categoria</p>
            <p className="mt-2 text-sm text-slate-400">Nuove foto dai cantieri vengono pubblicate regolarmente.</p>
          </div>
        )}
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/90 backdrop-blur-md p-4 sm:p-8"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative grid w-full max-w-4xl overflow-hidden rounded-2xl border border-gold/20 bg-panel md:grid-cols-2"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.img} alt={active.title} className="h-64 w-full object-cover md:h-full" />
              <div className="p-6 sm:p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-copper">{active.cat}</p>
                <h3 className="mt-2 font-display text-2xl font-bold uppercase text-cream" data-testid="lightbox-title">{active.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">{active.desc}</p>
                <ul className="mt-6 space-y-3 text-sm text-slate-300">
                  <li className="flex items-center gap-3"><MapPin className="h-4 w-4 text-gold" /> {active.loc}</li>
                  <li className="flex items-center gap-3"><Clock className="h-4 w-4 text-gold" /> Durata: {active.durata}</li>
                  <li className="flex items-center gap-3"><Layers className="h-4 w-4 text-gold" /> {active.materiali}</li>
                </ul>
              </div>
              <button
                data-testid="lightbox-close"
                onClick={() => setActive(null)}
                className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-ink/70 border border-gold/20 text-cream transition-colors hover:text-copper"
                aria-label="Chiudi"
              >
                <X className="h-5 w-5" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
